import React, { useState, useMemo, useCallback, useEffect } from 'react';
import api from '../services/api';

// ─── Design tokens ────────────────────────────────────────────────────────────
const navy  = '#1B3B5F';
const gold  = '#D4AF37';
const cream = '#F8F6F3';
const light = '#E8E4DF';
const sub   = '#6B6B6B';
const green = '#2D5F4F';
const orange= '#F59E0B';
const red   = '#DC3545';
const coral = '#E85D4A';

const inr  = n => `₹${Number(n||0).toLocaleString('en-IN')}`;
const inrL = n => { const v=Number(n||0); return v>=100000?`₹${(v/100000).toFixed(2)}L`:inr(v); };
const TODAY = new Date();
const daysDiff = d => d ? Math.floor((TODAY-new Date(d))/86400000) : null;
const emiSt = (date,paid) => {
  if (paid) return 'paid';
  if (!date) return 'future';
  const d = daysDiff(date);
  return d>7 ? 'overdue' : d>=0 ? 'due' : 'future';
};

// ─── Static data ──────────────────────────────────────────────────────────────
// Static data removed — loaded from API
const PG  = 10;

// ─── Components ───────────────────────────────────────────────────────────────
const TYPE_MAP={'4sharing':{bg:'rgba(27,59,95,0.12)',color:navy,lbl:'4S'},'3sharing':{bg:'rgba(45,95,79,0.12)',color:green,lbl:'3S'},'2sharing':{bg:'rgba(91,141,184,0.14)',color:'#3a6d97',lbl:'Twin'},'2sharingPremium':{bg:'rgba(212,175,55,0.18)',color:'#8B6914',lbl:'Prem'},'private':{bg:'rgba(232,93,74,0.12)',color:coral,lbl:'Pvt'}};
function TypePill({type}){const s=TYPE_MAP[type]||TYPE_MAP['3sharing'];return <span style={{display:'inline-block',padding:'3px 8px',borderRadius:4,fontSize:10.5,fontWeight:700,background:s.bg,color:s.color}}>{s.lbl}</span>;}

const PILL_MAP={'Paid':{bg:'rgba(45,95,79,0.12)',c:green},'Partial':{bg:'rgba(245,158,11,0.12)',c:orange},'Due':{bg:'rgba(91,141,184,0.14)',c:'#3a6d97'},'Overdue':{bg:'rgba(220,53,69,0.12)',c:red},'Active':{bg:'rgba(27,59,95,0.1)',c:navy},'Exit Pending':{bg:'rgba(212,175,55,0.15)',c:'#8B6914'},'Dues Adjusted':{bg:'rgba(220,53,69,0.12)',c:red},'Healthy':{bg:'rgba(45,95,79,0.12)',c:green},'Low':{bg:'rgba(245,158,11,0.12)',c:orange},'Critical':{bg:'rgba(220,53,69,0.12)',c:red},'UPI':{bg:'rgba(91,141,184,0.14)',c:'#3a6d97'},'Cash':{bg:'rgba(245,158,11,0.12)',c:orange},'Bank':{bg:'rgba(27,59,95,0.1)',c:navy},'✓ Reconciled':{bg:'rgba(45,95,79,0.12)',c:green}};
function Pill({v}){const s=PILL_MAP[v]||{bg:'rgba(100,100,100,0.1)',c:sub};return <span style={{display:'inline-flex',alignItems:'center',padding:'4px 10px',borderRadius:20,fontSize:10.5,fontWeight:700,letterSpacing:0.8,textTransform:'uppercase',background:s.bg,color:s.c}}>{v}</span>;}

const EMI_MAP={paid:{bg:green,ch:'✓'},due:{bg:'#3a6d97',ch:'D'},overdue:{bg:red,ch:'!'},future:{bg:'#C4BEB8',ch:'·'}};
function EmiDot({date,paid,idx}){const st=emiSt(date,paid);const s=EMI_MAP[st];return <div title={`EMI ${idx+1}${date?': '+date:''} — ${st}`} style={{width:20,height:20,borderRadius:'50%',background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:st==='future'?14:9,fontWeight:700,flexShrink:0,cursor:'default'}}>{s.ch}</div>;}

function SumCard({label,value,sub:s,accent,small}){return(
  <div style={{background:'white',borderRadius:10,padding:'20px 22px',boxShadow:'0 2px 10px rgba(0,0,0,0.05)',position:'relative',overflow:'hidden'}}>
    <div style={{position:'absolute',top:0,left:0,width:4,height:'100%',background:accent}}/>
    <div style={{fontSize:10,letterSpacing:1.5,textTransform:'uppercase',color:sub,marginBottom:8,fontWeight:600}}>{label}</div>
    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:small?22:30,fontWeight:600,color:accent===gold?'#8B6914':accent===orange?orange:accent===red?red:accent===green?green:navy,lineHeight:1,marginBottom:4}}>{value}</div>
    <div style={{fontSize:11,color:sub}}>{s}</div>
  </div>
);}

function HBar({label,pct,amount,color}){return(
  <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
    <div style={{width:140,fontSize:12,color:sub,fontWeight:500,flexShrink:0}}>{label}</div>
    <div style={{flex:1,height:8,background:light,borderRadius:4,overflow:'hidden'}}><div style={{width:`${pct}%`,height:'100%',background:color,borderRadius:4}}/></div>
    <div style={{width:38,fontSize:12,fontWeight:700,textAlign:'right',flexShrink:0,color}}>{pct}%</div>
    <div style={{width:72,fontSize:11,color:sub,textAlign:'right',flexShrink:0}}>{amount}</div>
  </div>
);}

function LineChart({trend}){
  // trend = [{month:'Jul',collected:8.64,pending:0.36}, ...]
  const data = trend && trend.length > 0 ? trend : [];
  if (!data.length) return <div style={{height:170,display:'flex',alignItems:'center',justifyContent:'center',color:'#C4BEB8',fontSize:13}}>No trend data</div>;
  const W=560,H=170,PL=36,PR=14,PT=14,PB=28,cW=W-PL-PR,cH=H-PT-PB,n=data.length;
  const col=data.map(d=>d.collected||0), pen=data.map(d=>d.pending||0);
  const maxV=Math.max(10,...col,...pen);
  const xOf=i=>PL+(n>1?i/(n-1):0.5)*cW, yOf=v=>PT+cH-(v/maxV)*cH;
  const curve=arr=>arr.map((v,i)=>{if(i===0)return`M ${xOf(0)} ${yOf(v)}`;const mx=(xOf(i-1)+xOf(i))/2;return`C ${mx} ${yOf(arr[i-1])}, ${mx} ${yOf(v)}, ${xOf(i)} ${yOf(v)}`;}).join(' ');
  const cp=curve(col),pp=curve(pen);
  const gridVals=[0,Math.round(maxV*0.25),Math.round(maxV*0.5),Math.round(maxV*0.75),Math.round(maxV)];
  return(
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H}}>
      {gridVals.map((v,i)=><g key={i}><line x1={PL} y1={yOf(v)} x2={W-PR} y2={yOf(v)} stroke={light} strokeWidth={1} strokeDasharray="4 4"/><text x={PL-4} y={yOf(v)+3} textAnchor="end" fontSize={10} fill={sub} fontFamily="Work Sans,sans-serif">{v}</text></g>)}
      {data.map((d,i)=><text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize={10} fill={sub} fontFamily="Work Sans,sans-serif">{d.month}</text>)}
      <path d={cp+` L ${xOf(n-1)} ${PT+cH} L ${xOf(0)} ${PT+cH} Z`} fill={navy} opacity="0.07"/>
      <path d={cp} stroke={navy} fill="none" strokeWidth="2.5" strokeLinecap="round"/>
      <path d={pp} stroke={coral} fill="none" strokeWidth="1.8" strokeDasharray="4 3" strokeLinecap="round"/>
      {col.map((v,i)=><circle key={i} cx={xOf(i)} cy={yOf(v)} r="3" fill={navy} stroke="white" strokeWidth="1.5"/>)}
    </svg>
  );
}

function Divider({label}){return(
  <div style={{display:'flex',alignItems:'center',gap:16,margin:'8px 0 20px'}}>
    <div style={{flex:1,height:1,background:light}}/><div style={{fontSize:11,letterSpacing:2.5,textTransform:'uppercase',color:sub,fontWeight:600}}>{label}</div><div style={{flex:1,height:1,background:light}}/>
  </div>
);}

function RRow({label,value,color}){return(
  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 0',borderBottom:`1px solid ${light}`,fontSize:13}}>
    <span style={{color:sub}}>{label}</span>
    <span style={{fontFamily:'monospace',fontWeight:700,fontSize:14,color:color||'#2C2C2C'}}>{value}</span>
  </div>
);}

function AlertStrip({title,items,color,bg}){
  if(!items||items.length===0)return null;
  const ac=color||red, abg=bg||'#FFF8F8';
  return(
    <div style={{background:abg,border:`1px solid ${ac}30`,borderLeft:`4px solid ${ac}`,borderRadius:'0 10px 10px 0',padding:'14px 20px',marginBottom:22}}>
      <div style={{fontSize:11,fontWeight:700,color:ac,letterSpacing:1.5,textTransform:'uppercase',marginBottom:10}}>{title}</div>
      <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
        {items.map((it,i)=>(
          <div key={i} style={{background:'white',borderRadius:8,padding:'10px 14px',border:`1px solid ${light}`,minWidth:150}}>
            <div style={{fontSize:12,fontWeight:700,color:navy}}>{it.name}</div>
            <div style={{fontSize:11,color:sub,margin:'2px 0'}}>{it.detail||it.room}</div>
            <div style={{fontSize:15,fontWeight:700,color:ac,fontFamily:'monospace'}}>{it.amount||inr(it.balance)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
const sel = {padding:'9px 14px',borderRadius:7,border:`1.5px solid ${light}`,fontFamily:'Work Sans,sans-serif',fontSize:12.5,color:'#2C2C2C',background:'white',cursor:'pointer',outline:'none'};
const th0 = {padding:'11px 14px',fontSize:10,letterSpacing:1.5,textTransform:'uppercase',color:sub,fontWeight:700,borderBottom:`1px solid ${light}`,whiteSpace:'nowrap',cursor:'pointer',background:cream};
const td0 = {padding:'12px 14px',fontSize:13,borderBottom:`1px solid ${light}`,color:'#2C2C2C',verticalAlign:'middle'};

export default function FinanceReport() {
  const [tab,       setTab]       = useState('fees');
  const [period,    setPeriod]    = useState('quarter');
  const [ay,        setAY]        = useState('2025-26');
  const [dateOffset,setDateOffset]= useState(0);
  const [fStatus, setFStatus] = useState('all');
  const [fType,   setFType]   = useState('all');
  const [fPlan,   setFPlan]   = useState('all');
  const [fQ,      setFQ]      = useState('');
  const [fSort,   setFSort]   = useState({col:'name',dir:'asc'});
  const [fPage,   setFPage]   = useState(1);
  const [pcSt,    setPCSt]    = useState('all');
  const [pcQ,     setPCQ]     = useState('');
  const [depSt,   setDepSt]   = useState('all');
  const [depType, setDepType] = useState('all');
  const [depQ,    setDepQ]    = useState('');

  // ── Real data from API ────────────────────────────────────────────────────
  const [feesData,   setFeesData]   = useState({ totalBilled:0, totalCollected:0, pending:0, overdue:0, emiCount:0, collectionRate:0, collectionByHead:[], ledger:[] });
  const [pcData,     setPcData]     = useState({ totalPool:0, totalTopup:0, totalSpent:0, lowBalCount:0, spendCanteen:0, spendLaundry:0, spendElec:0, lowBalAlerts:[], ledger:[], topupHistory:[] });
  const [depData,    setDepData]    = useState({ totalHeld:0, totalDeductions:0, netRefundable:0, exitCount:0, ledger:[] });
  const [loading,    setLoading]    = useState({ fees:false, paycard:false, deposit:false });

  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const monthNames   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const { from: rangeFrom, to: rangeTo, label: periodLabel } = React.useMemo(() => {
    const now  = new Date();
    const nowY = now.getFullYear();
    const nowM = now.getMonth(); // 0=Jan ... 11=Dec
    const today = `${nowY}-${String(nowM+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

    // Format YYYY-MM-DD without timezone issues
    const fmt = (y, m, d) => `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    const lastDay = (y, m) => new Date(y, m+1, 0).getDate();

    if (period === 'month') {
      // Use Date arithmetic to safely handle month overflow/underflow
      const d  = new Date(nowY, nowM + dateOffset, 1);
      const y  = d.getFullYear();
      const m  = d.getMonth();
      const to = fmt(y, m, lastDay(y, m));
      return {
        from:  fmt(y, m, 1),
        to:    to > today ? today : to,  // cap future months at today
        label: `${monthNames[m]} ${y}`
      };
    }

    if (period === 'quarter') {
      // Calculate target quarter from current quarter + offset
      // Total months from Jan 2000: use absolute month arithmetic
      const totalMonths = nowY * 12 + nowM + dateOffset * 3;
      const tY    = Math.floor(totalMonths / 12);
      const tM    = totalMonths % 12;                    // first month of offset period
      const startM = Math.floor(tM / 3) * 3;           // round down to quarter start
      const endM   = startM + 2;
      const qNum   = Math.floor(startM / 3) + 1;       // 1-4
      const qLabels = ['Jan–Mar','Apr–Jun','Jul–Sep','Oct–Dec'];
      const to = fmt(tY, endM, lastDay(tY, endM));
      return {
        from:  fmt(tY, startM, 1),
        to:    to > today ? today : to,  // cap future at today
        label: `Q${qNum} · ${qLabels[qNum-1]} ${tY}`
      };
    }

    // year — full academic year Jul to Jun
    const y1 = parseInt(ay.split('-')[0]) + dateOffset;
    const to = fmt(y1+1, 5, 30);
    return {
      from:  fmt(y1,   6, 1),
      to:    to > today ? today : to,  // cap future at today
      label: `A.Y. ${y1}–${String(y1+1).slice(-2)}`
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, dateOffset, ay]);


    // ✅ Single useEffect — fetch when tab OR date range changes
  useEffect(() => {
    // Reset fees data when period changes
    if (tab === 'fees') {
      setFeesData({ totalBilled:0, totalCollected:0, pending:0, overdue:0, emiCount:0, collectionRate:0, collectionByHead:[], trend:[], ledger:[] });
      setLoading(l=>({...l,fees:true}));
      console.log('Fetching fees:', rangeFrom, 'to', rangeTo);
      api.get(`/reports/finance/fees?from=${rangeFrom}&to=${rangeTo}`)
        .then(r => { console.log('Fees data:', r.data); setFeesData(r.data); })
        .catch(e => console.error('Fees report error:', e))
        .finally(() => setLoading(l=>({...l,fees:false})));
    }
    if (tab === 'paycard') {
      setLoading(l=>({...l,paycard:true}));
      api.get('/reports/finance/paycard')
        .then(r => setPcData(r.data))
        .catch(e => console.error('PayCard report error:', e))
        .finally(() => setLoading(l=>({...l,paycard:false})));
    }
    if (tab === 'deposit') {
      setLoading(l=>({...l,deposit:true}));
      api.get('/reports/finance/deposit')
        .then(r => setDepData(r.data))
        .catch(e => console.error('Deposit report error:', e))
        .finally(() => setLoading(l=>({...l,deposit:false})));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, rangeFrom, rangeTo, ay, period, dateOffset]);



  // Calculate actual date range based on period + offset
  const feesF = useMemo(()=>(feesData.ledger||[]).filter(s=>{
    if(!s) return false;
    if(fStatus!=='all'&&(s.status||'')!==fStatus)return false;
    if(fType!=='all'&&(s.roomType||s.type||'')!==fType)return false;
    if(fPlan!=='all'&&(s.plan||'')!==fPlan)return false;
    const q=fQ.toLowerCase();
    if(q&&!(s.studentName||s.name||'').toLowerCase().includes(q)&&!(s.admissionNumber||s.sid||'').toLowerCase().includes(q))return false;
    return true;
  }).sort((a,b)=>{if(!a||!b)return 0;const d=fSort.dir==='asc'?1:-1;if(fSort.col==='name')return d*(a.studentName||a.name||'').localeCompare(b.studentName||b.name||'');if(fSort.col==='room')return d*(a.room||'').localeCompare(b.room||'');if(fSort.col==='status')return d*(a.status||'').localeCompare(b.status||'');if(fSort.col==='sid')return d*(a.admissionNumber||a.sid||'').localeCompare(b.admissionNumber||b.sid||'');return d*((a[fSort.col]||0)-(b[fSort.col]||0));}),[fStatus,fType,fPlan,fQ,fSort,feesData]);

  const feesPages=Math.ceil(feesF.length/PG)||1;
  const feesPage=feesF.slice((fPage-1)*PG,fPage*PG);
  const sortF=col=>{setFSort(s=>({col,dir:s.col===col&&s.dir==='asc'?'desc':'asc'}));setFPage(1);};
  const arr=col=>fSort.col===col?(fSort.dir==='asc'?'↑':'↓'):'↕';

  const fs=useMemo(()=>({billed:feesData.totalBilled||0,collected:feesData.totalCollected||0,pending:feesData.pending||0,overdue:feesData.overdue||0,emi:feesData.emiCount||0,collRate:feesData.collectionRate||0}),[feesData]);

  const odAlerts=useMemo(()=>(feesData.ledger||[]).filter(s=>s.status==='Overdue').map(s=>{const e=(s.emis||[]).find(e=>emiSt(e.date,e.paid)==='overdue');return{name:s.studentName||s.name,detail:`${s.room} · ${s.roomType||s.type} · ${e?daysDiff(e.date):0} days overdue`,amount:inr(s.balance)};}),[feesData]);
  const pcF=useMemo(()=>(pcData.ledger||[]).filter(r=>{if(pcSt!=='all'&&r.bstatus!==pcSt)return false;const q=pcQ.toLowerCase();if(q&&!(r.studentName||r.name||'').toLowerCase().includes(q))return false;return true;}),[pcSt,pcQ,pcData]);
  const lowBal=useMemo(()=>pcData.lowBalAlerts||[],[pcData]);
  const depF=useMemo(()=>(depData.ledger||[]).filter(r=>{if(depSt!=='all'&&r.status!==depSt)return false;if(depType!=='all'&&r.roomType!==depType)return false;const q=depQ.toLowerCase();if(q&&!(r.studentName||r.name||'').toLowerCase().includes(q))return false;return true;}),[depSt,depType,depQ,depData]);

  const printReport = useCallback(() => {
    // Add temporary print styles to hide sidebar and show full width
    const style = document.createElement('style');
    style.id = 'finance-print-style';
    style.innerHTML = `
      @media print {
        .sidebar, .top-bar, nav, aside,
        [class*="sidebar"], [class*="nav-"] {
          display: none !important;
        }
        .main-content, main {
          margin-left: 0 !important;
          width: 100% !important;
        }
        body { background: white !important; }
        button { display: none !important; }
        @page { margin: 1cm; size: A4 landscape; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => {
      const el = document.getElementById('finance-print-style');
      if (el) el.remove();
    }, 1000);
  }, []);

  const exportXLSX=useCallback(()=>{
    try{
      import('xlsx').then(XLSX=>{
        const wb=XLSX.utils.book_new();
        if(tab==='fees'){
          const hd=['Student ID','Student Name','Room','Room Type','Accommodation','Food','Maintenance','Total Fees','Paid','Balance','Plan','Status'];
          const rows=feesF.map(s=>[
            s.admissionNumber||s.sid||'',
            s.studentName||s.name||'',
            s.room||'',
            s.roomType||s.type||'',
            s.accommodation||s.accomm||0,
            s.food||0,
            s.maintenance||s.maint||0,
            s.total||0,
            s.paid||0,
            s.balance||0,
            s.plan||'',
            s.status||''
          ]);
          const ws=XLSX.utils.aoa_to_sheet([hd,...rows]);
          ws['!cols']=[{wch:12},{wch:22},{wch:10},{wch:10},{wch:14},{wch:12},{wch:12},{wch:12},{wch:12},{wch:12},{wch:8},{wch:10}];
          XLSX.utils.book_append_sheet(wb,ws,'Fee Ledger');
          // Summary sheet
          const sum=[
            ['Omkara Hostel — Student Fees Report'],
            ['Period', periodLabel],
            ['Generated', new Date().toLocaleDateString('en-IN')],
            [],
            ['SUMMARY'],
            ['Total Billed', feesData.totalBilled||0],
            ['Collected', feesData.totalCollected||0],
            ['Pending', feesData.pending||0],
            ['Overdue', feesData.overdue||0],
            ['Collection Rate', `${feesData.collectionRate||0}%`],
          ];
          XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(sum),'Summary');
          XLSX.writeFile(wb,`Omkara_Fees_${periodLabel.replace(/[·\s]/g,'_')}.xlsx`);
        }
        else if(tab==='paycard'){
          const hd=['Student ID','Student Name','Room','Room Type','Opening','Top-Ups','Canteen','Laundry','Electricity','Balance','Status'];
          const rows=pcF.map(r=>[
            r.admissionNumber||r.sid||'',
            r.studentName||r.name||'',
            r.room||'',
            r.roomType||r.type||'',
            r.opening||r.open||0,
            r.topup||0,
            r.canteen||0,
            r.laundry||0,
            r.electricity||r.elec||0,
            r.balance||0,
            r.bstatus||r.status||''
          ]);
          const ws=XLSX.utils.aoa_to_sheet([hd,...rows]);
          ws['!cols']=[{wch:12},{wch:22},{wch:10},{wch:10},{wch:12},{wch:10},{wch:10},{wch:10},{wch:12},{wch:10},{wch:10}];
          XLSX.utils.book_append_sheet(wb,ws,'Pay Card Ledger');
          XLSX.writeFile(wb,'Omkara_PayCard.xlsx');
        }
        else{
          const hd=['Student ID','Student Name','Room','Room Type','Admission Date','Deposit Paid','Deductions','Reason','Net Refundable','Status'];
          const rows=depF.map(r=>[
            r.admissionNumber||r.sid||'',
            r.studentName||r.name||'',
            r.room||'',
            r.roomType||r.type||'',
            r.admissionDate||r.admdate||'',
            r.deposit||0,
            r.deduction||0,
            r.reason||'',
            r.net||0,
            r.status||''
          ]);
          const ws=XLSX.utils.aoa_to_sheet([hd,...rows]);
          ws['!cols']=[{wch:12},{wch:22},{wch:10},{wch:10},{wch:14},{wch:14},{wch:12},{wch:16},{wch:14},{wch:12}];
          XLSX.utils.book_append_sheet(wb,ws,'Security Deposit');
          XLSX.writeFile(wb,'Omkara_SecurityDeposit.xlsx');
        }
      });
    }catch(e){console.error(e);}
  },[tab,feesF,pcF,depF]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{fontFamily:'Work Sans,sans-serif'}}>

      {/* Top action buttons */}
      <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginBottom:20}}>
        <button onClick={()=>window.print()} style={{padding:'10px 22px',background:'white',color:navy,border:`1.5px solid ${light}`,borderRadius:6,fontFamily:'Work Sans,sans-serif',fontWeight:600,fontSize:12,textTransform:'uppercase',letterSpacing:1,cursor:'pointer'}}>🖨 Print</button>
        <button onClick={exportXLSX} style={{padding:'10px 22px',background:gold,color:navy,border:'none',borderRadius:6,fontFamily:'Work Sans,sans-serif',fontWeight:600,fontSize:12,textTransform:'uppercase',letterSpacing:1,cursor:'pointer'}}>⬇ Export XLSX</button>
        <button onClick={printReport} style={{padding:'10px 22px',background:navy,color:'white',border:'none',borderRadius:6,fontFamily:'Work Sans,sans-serif',fontWeight:600,fontSize:12,textTransform:'uppercase',letterSpacing:1,cursor:'pointer'}}>⬇ Export PDF</button>
      </div>

      {/* Sub-tabs */}
      <div style={{display:'flex',background:'white',borderRadius:10,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:28,overflow:'hidden'}}>
        {[{id:'fees',icon:'💰',label:'Student Fees',sub:'Accommodation · Food · Maintenance'},{id:'paycard',icon:'💳',label:'Pay Card',sub:'Canteen · Laundry · Electricity'},{id:'deposit',icon:'🔐',label:'Security Deposit',sub:'Held · Deductions · Refundable'}].map(t=>(
          <div key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:'16px 20px',textAlign:'center',cursor:'pointer',borderBottom:`3px solid ${tab===t.id?gold:'transparent'}`,background:tab===t.id?'rgba(212,175,55,0.04)':'white',borderRight:`1px solid ${light}`,transition:'all 0.2s'}}>
            <div style={{fontSize:20,marginBottom:4}}>{t.icon}</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:15,fontWeight:600,color:tab===t.id?navy:sub}}>{t.label}</div>
            <div style={{fontSize:10,letterSpacing:1,textTransform:'uppercase',color:sub,opacity:0.7,marginTop:2}}>{t.sub}</div>
          </div>
        ))}
      </div>

      {/* ══ FEES TAB ══════════════════════════════════════════════════════════ */}
      {tab==='fees'&&(<>
        {loading.fees && <div style={{textAlign:'center',padding:40,color:sub}}>Loading fees data…</div>}

        {/* Header */}
        <div style={{background:'white',borderRadius:12,padding:'24px 32px',boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:24,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
          <div>
            <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:24,fontWeight:500,color:navy,marginBottom:4}}>Student Fees Report</h2>
            <p style={{fontSize:13,color:sub}}>Academic Year {ay} · {periodLabel}</p>
          </div>
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <select style={sel} value={ay} onChange={e=>{setAY(e.target.value);setDateOffset(0);}}>
              {Array.from({length:4},(_,i)=>{const y=currentYear-i;return <option key={y} value={`${y-1}-${String(y).slice(-2)}`}>A.Y. {y-1}–{String(y).slice(-2)}</option>;}).reverse()}
            </select>
            <div style={{display:'flex',background:cream,borderRadius:8,padding:4,gap:2}}>
              {['month','quarter','year'].map(p=>(
                <button key={p} onClick={()=>{setPeriod(p);setDateOffset(0);}} style={{padding:'7px 16px',borderRadius:6,fontSize:11,fontWeight:600,letterSpacing:0.8,textTransform:'uppercase',cursor:'pointer',fontFamily:'Work Sans,sans-serif',border:'none',background:period===p?navy:'transparent',color:period===p?'white':sub}}>
                  {p==='month'?'Month':p==='quarter'?'Quarter':'Year'}
                </button>
              ))}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,background:'white',border:`1.5px solid ${light}`,borderRadius:8,padding:'6px 14px'}}>
              <button onClick={()=>setDateOffset(o=>o-1)} style={{width:28,height:28,borderRadius:6,border:'none',background:cream,cursor:'pointer',fontSize:14,color:navy}}>‹</button>
              <span style={{fontSize:13,fontWeight:600,color:navy,minWidth:160,textAlign:'center'}}>{periodLabel}</span>
              <button onClick={()=>setDateOffset(o=>o+1)} style={{width:28,height:28,borderRadius:6,border:'none',background:cream,cursor:'pointer',fontSize:14,color:navy}}>›</button>
            </div>
          </div>
        </div>

        {/* 5 summary cards */}

        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:16,marginBottom:24}}>
          <SumCard label="Total Billed" value={inrL(fs.billed)}    sub={`${(feesData.ledger||[]).length} students`}                          accent={navy}/>
          <SumCard label="Collected" value={inrL(fs.collected)} sub={`${periodLabel} · ${fs.collRate}% rate`}   accent={green}/>
          <SumCard label="Pending / Due" value={inrL(fs.pending)}   sub={`${(feesData.ledger||[]).filter(r=>["Due","Partial"].includes(r.status)).length} students`}                                                     accent={orange} small/>
          <SumCard label="Overdue" value={inr(fs.overdue)}    sub={`${(feesData.ledger||[]).filter(r=>r.status==="Overdue").length} students · >7 days past EMI`}                                  accent={red}    small/>
          <SumCard label="On EMI Plan"   value={String(fs.emi)}     sub={`of ${(feesData.ledger||[]).length} students`}                               accent={gold}/>
        </div>

        {/* Charts */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:22}}>
          <div style={{background:'white',borderRadius:12,padding:'24px 28px',boxShadow:'0 2px 12px rgba(0,0,0,0.06)'}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:17,fontWeight:600,color:navy,marginBottom:14}}>Collection by Fee Head</div>
{(feesData.collectionByHead||[
              {label:'Accommodation',billed:1,paid:0},
              {label:'Food Charges',billed:1,paid:0},
              {label:'Maintenance',billed:1,paid:0}
            ]).map((h,i)=>{
              const pct=h.billed>0?Math.round(h.paid/h.billed*100):0;
              const colors=[navy,green,gold];
              return <HBar key={i} label={h.label} pct={pct} amount={inrL(h.paid)} color={colors[i]||navy}/>;
            })}
          </div>
          <div style={{background:'white',borderRadius:12,padding:'24px 28px',boxShadow:'0 2px 12px rgba(0,0,0,0.06)'}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:17,fontWeight:600,color:navy,marginBottom:8}}>Monthly Collection Trend</div>
            <div style={{display:'flex',gap:16,marginBottom:10,flexWrap:'wrap'}}>
              <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:sub}}><div style={{width:10,height:10,borderRadius:'50%',background:navy}}/>Collected (₹L)</div>
              <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:sub}}><div style={{width:16,height:3,background:coral}}/>Pending</div>
            </div>
            <LineChart trend={feesData.trend||[]}/>
          </div>
        </div>

        {/* Overdue alert */}
        <AlertStrip title="⚠ Overdue — EMI dates missed by more than 7 days" items={odAlerts}/>

        {/* EMI legend */}
        <div style={{display:'flex',gap:14,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:sub}}>EMI Status:</span>
          {[{bg:green,ch:'✓',l:'Paid'},{bg:'#3a6d97',ch:'D',l:'Due (≤7 days)'},{bg:red,ch:'!',l:'Overdue (>7 days)'},{bg:'#C4BEB8',ch:'·',l:'Future'}].map((s,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
              <div style={{width:20,height:20,borderRadius:'50%',background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:s.ch==='·'?14:9,fontWeight:700}}>{s.ch}</div>{s.l}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginBottom:22}}>
          <span style={{fontSize:11,letterSpacing:1.5,textTransform:'uppercase',color:sub,fontWeight:600}}>Filter by:</span>
          <select style={sel} value={fStatus} onChange={e=>{setFStatus(e.target.value);setFPage(1);}}>
            <option value="all">All Statuses</option><option value="Paid">Paid</option><option value="Partial">Partial</option><option value="Due">Due</option><option value="Overdue">Overdue</option>
          </select>
          <select style={sel} value={fType} onChange={e=>{setFType(e.target.value);setFPage(1);}}>
            <option value="all">All Types</option><option value="4sharing">4 Sharing</option><option value="3sharing">3 Sharing</option><option value="2sharing">Twin</option><option value="2sharingPremium">Premium</option><option value="private">Private</option>
          </select>
          <select style={sel} value={fPlan} onChange={e=>{setFPlan(e.target.value);setFPage(1);}}>
            <option value="all">All Plans</option><option value="full">Full Payment</option><option value="emi">EMI</option>
          </select>
          <button onClick={()=>{setFStatus('all');setFType('all');setFPlan('all');setFQ('');setFPage(1);}} style={{padding:'8px 14px',background:'transparent',border:`1.5px solid ${light}`,borderRadius:6,cursor:'pointer',fontSize:11,fontWeight:600,color:navy,fontFamily:'Work Sans,sans-serif',marginLeft:'auto'}}>✕ Clear</button>
        </div>

        {/* Fee ledger table */}
        <div style={{background:'white',borderRadius:12,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',overflow:'hidden'}}>
          <div style={{padding:'18px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:`1px solid ${light}`,flexWrap:'wrap',gap:12}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:18,fontWeight:600,color:navy}}>Fee Ledger</div>
            <div style={{display:'flex',alignItems:'center',gap:8,background:cream,borderRadius:8,padding:'8px 14px'}}>
              <span style={{color:sub}}>🔍</span>
              <input placeholder="Search name or ID…" value={fQ} onChange={e=>{setFQ(e.target.value);setFPage(1);}} style={{border:'none',background:'transparent',fontFamily:'Work Sans,sans-serif',fontSize:13,color:'#2C2C2C',outline:'none',width:180}}/>
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <th style={{...th0,textAlign:'left'}} onClick={()=>sortF('sid')}>ID {arr('sid')}</th>
                  <th style={{...th0,textAlign:'left'}} onClick={()=>sortF('name')}>Student Name {arr('name')}</th>
                  <th style={{...th0,textAlign:'left'}} onClick={()=>sortF('room')}>Room {arr('room')}</th>
                  <th style={{...th0,textAlign:'left'}}>Type</th>
                  <th style={{...th0,textAlign:'right'}} onClick={()=>sortF('accomm')}>Accomm. {arr('accomm')}</th>
                  <th style={{...th0,textAlign:'right'}} onClick={()=>sortF('food')}>Food {arr('food')}</th>
                  <th style={{...th0,textAlign:'right'}} onClick={()=>sortF('maint')}>Maint. {arr('maint')}</th>
                  <th style={{...th0,textAlign:'right'}} onClick={()=>sortF('total')}>Total {arr('total')}</th>
                  <th style={{...th0,textAlign:'right'}} onClick={()=>sortF('paid')}>Paid {arr('paid')}</th>
                  <th style={{...th0,textAlign:'right'}} onClick={()=>sortF('balance')}>Balance {arr('balance')}</th>
                  <th style={{...th0,textAlign:'left'}}>EMI (1–4)</th>
                  <th style={{...th0,textAlign:'left'}} onClick={()=>sortF('status')}>Status {arr('status')}</th>
                </tr>
              </thead>
              <tbody>
                {feesPage.length===0?(
                  <tr><td colSpan={12} style={{padding:'40px',textAlign:'center',color:'#C4BEB8',fontStyle:'italic'}}>No records found.</td></tr>
                ):feesPage.map((s,i)=>{
                  const bc=s.balance===0?green:s.status==='Overdue'?red:orange;
                  return(
                    <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='rgba(27,59,95,0.02)'} onMouseLeave={e=>e.currentTarget.style.background=''}>
                      <td style={{...td0,fontFamily:'monospace',fontSize:12,color:sub}}>{s.admissionNumber||s.sid}</td>
                      <td style={{...td0,fontWeight:600,color:navy}}>{s.studentName||s.name}</td>
                      <td style={td0}>{s.room||'—'}</td>
                      <td style={td0}><TypePill type={s.roomType||s.type}/></td>
                      <td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(s.accomm)}</td>
                      <td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(s.food)}</td>
                      <td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(s.maint)}</td>
                      <td style={{...td0,textAlign:'right',fontFamily:'monospace',fontWeight:700}}>{inr(s.total)}</td>
                      <td style={{...td0,textAlign:'right',fontFamily:'monospace',color:green}}>{inr(s.paid)}</td>
                      <td style={{...td0,textAlign:'right',fontFamily:'monospace',fontWeight:700,color:bc}}>{inr(s.balance)}</td>
                      <td style={td0}><div style={{display:'flex',gap:4}}>{s.emis.map((e,j)=><EmiDot key={j} date={e.date} paid={e.paid} idx={j}/>)}</div></td>
                      <td style={td0}><Pill v={s.status}/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{padding:'13px 22px',display:'flex',alignItems:'center',justifyContent:'space-between',borderTop:`1px solid ${light}`,background:cream}}>
            <span style={{fontSize:12,color:sub}}>Showing {feesF.length===0?0:(fPage-1)*PG+1}–{Math.min(fPage*PG,feesF.length)} of {feesF.length} records</span>
            <div style={{display:'flex',gap:4}}>
              {[{l:'‹',p:fPage-1},...Array.from({length:Math.min(feesPages,5)},(_,i)=>({l:String(i+1),p:i+1})),{l:'›',p:fPage+1}].map(({l,p},i)=>{
                const dis=(l==='‹'&&fPage===1)||(l==='›'&&fPage>=feesPages);
                const act=String(p)===String(fPage);
                return <button key={i} onClick={()=>{if(!dis&&p>=1&&p<=feesPages)setFPage(p);}} disabled={dis} style={{width:32,height:32,borderRadius:6,border:`1.5px solid ${act?navy:light}`,background:act?navy:'white',color:act?'white':sub,fontSize:12,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'Work Sans,sans-serif',opacity:dis?0.4:1}}>{l}</button>;
              })}
            </div>
          </div>
        </div>
      </>)}

      {/* ══ PAYCARD TAB ═══════════════════════════════════════════════════════ */}
      {tab==='paycard'&&(<>
        {loading.paycard && <div style={{textAlign:'center',padding:40,color:sub}}>Loading pay card data…</div>}
        <div style={{background:'white',borderRadius:12,padding:'24px 32px',boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:24,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
          <div><h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:24,fontWeight:500,color:navy,marginBottom:4}}>Pay Card Report</h2><p style={{fontSize:13,color:sub}}>Student funds held in trust · Closed-loop card · Not hostel income</p></div>
          <div style={{display:'flex',gap:10}}><select style={sel}><option>A.Y. 2025–26</option><option>A.Y. 2024–25</option></select></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:16,marginBottom:24}}>
          <SumCard label="Total Pool (Liability)"  value={inrL(pcData.totalPool)} sub={`${(pcData.ledger||[]).length} students · current`}       accent={navy}/>
          <SumCard label="Admission Loads"          value={inrL(pcData.admissionLoads||pcData.totalTopup)} sub={`${(pcData.ledger||[]).length} × ₹15,000`}                   accent={green}/>
          <SumCard label="Admin Top-Ups"            value={inrL(pcData.adminTopups||0)} sub={`${(pcData.topupHistory||[]).length} transactions`}     accent={gold}   small/>
          <SumCard label="Total Spent (May)"        value={inrL(pcData.totalSpent)} sub="Canteen + Laundry + Elec."      accent={coral}  small/>
          <SumCard label="Low Balance Alerts"       value={String(pcData.lowBalCount||0)} sub="Below ₹2,000"                   accent={orange}/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:22}}>
          {[
            {l:'Canteen',            c:navy,  v:pcData.spendCanteen||0, spend:pcData.totalSpent||0},
            {l:'Laundry',            c:green, v:pcData.spendLaundry||0, spend:pcData.totalSpent||0},
            {l:'Electricity (Pro-rata)',c:gold,v:pcData.spendElec||0,   spend:pcData.totalSpent||0},
          ].map(x=>{
            const pct = x.spend>0 ? Math.round(x.v/x.spend*100) : 0;
            return(
            <div key={x.l} style={{background:'white',borderRadius:12,padding:20,boxShadow:'0 2px 12px rgba(0,0,0,0.06)'}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase',color:sub,marginBottom:4}}>{x.l}</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:26,fontWeight:600,color:navy,marginBottom:4}}>{inrL(x.v)}</div>
              <div style={{fontSize:12,color:sub,marginBottom:10}}>{x.l==='Electricity (Pro-rata)'?'Wing-wise billing':`${pct}% of spend`}</div>
              <div style={{height:5,background:light,borderRadius:3}}><div style={{width:`${pct}%`,height:'100%',background:x.c,borderRadius:3}}/></div>
            </div>
          );})}
        </div>
        <AlertStrip title="⚠ Low Pay Card Balance — Immediate top-up required" items={lowBal} color={orange} bg="#FFFBF0"/>
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginBottom:22}}>
          <span style={{fontSize:11,letterSpacing:1.5,textTransform:'uppercase',color:sub,fontWeight:600}}>Filter by:</span>
          <select style={sel} value={pcSt} onChange={e=>setPCSt(e.target.value)}><option value="all">All Balances</option><option value="Healthy">Healthy</option><option value="Low">Low</option><option value="Critical">Critical</option></select>
          <button onClick={()=>{setPCSt('all');setPCQ('');}} style={{padding:'8px 14px',background:'transparent',border:`1.5px solid ${light}`,borderRadius:6,cursor:'pointer',fontSize:11,fontWeight:600,color:navy,fontFamily:'Work Sans,sans-serif',marginLeft:'auto'}}>✕ Clear</button>
        </div>
        <div style={{background:'white',borderRadius:12,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',overflow:'hidden',marginBottom:22}}>
          <div style={{padding:'18px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:`1px solid ${light}`,flexWrap:'wrap',gap:12}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:18,fontWeight:600,color:navy}}>Pay Card Ledger</div>
            <div style={{display:'flex',alignItems:'center',gap:8,background:cream,borderRadius:8,padding:'8px 14px'}}><span>🔍</span><input placeholder="Search…" value={pcQ} onChange={e=>setPCQ(e.target.value)} style={{border:'none',background:'transparent',fontFamily:'Work Sans,sans-serif',fontSize:13,outline:'none',width:150}}/></div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['ID','Student Name','Room','Type','Opening','Top-Ups','Canteen','Laundry','Electricity','Balance','Status'].map((h,i)=><th key={i} style={{...th0,textAlign:i>=4&&i<=9?'right':'left'}}>{h}</th>)}</tr></thead>
              <tbody>{pcF.map((r,i)=>{const bc=r.bstatus==='Healthy'?green:r.bstatus==='Low'?orange:red;return(<tr key={i} onMouseEnter={e=>e.currentTarget.style.background='rgba(27,59,95,0.02)'} onMouseLeave={e=>e.currentTarget.style.background=''}><td style={{...td0,fontFamily:'monospace',fontSize:12,color:sub}}>{r.admissionNumber||r.sid}</td><td style={{...td0,fontWeight:600,color:navy}}>{r.studentName||r.name}</td><td style={td0}>{r.room||'—'}</td><td style={td0}><TypePill type={r.roomType||r.type||'3sharing'}/></td><td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(r.opening||r.open||0)}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace',color:green,fontWeight:600}}>{(r.topup||0)>0?'+'+inr(r.topup):'—'}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(r.canteen||0)}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(r.laundry||0)}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(r.electricity||r.elec||0)}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace',fontWeight:700,color:bc}}>{inr(r.balance||0)}</td><td style={td0}><Pill v={r.bstatus}/></td></tr>);})}</tbody>
            </table>
          </div>
          <div style={{padding:'13px 22px',display:'flex',alignItems:'center',justifyContent:'space-between',borderTop:`1px solid ${light}`,background:cream,flexWrap:'wrap',gap:8}}>
            <span style={{fontSize:12,color:sub}}>Showing {pcF.length} of {pcData.ledger.length} students</span>
            <span style={{fontSize:11,color:sub,fontStyle:'italic'}}>Balance = Opening + Top-Ups − Canteen − Laundry − Electricity</span>
          </div>
        </div>
        <Divider label="Admin Top-Up History"/>
        <div style={{background:'white',borderRadius:12,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',overflow:'hidden',marginBottom:22}}>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Date','Student Name','Room','Type','Amount','Mode','Reference No.','Credited By','Balance After'].map((h,i)=><th key={i} style={{...th0,textAlign:i===4||i===8?'right':'left'}}>{h}</th>)}</tr></thead>
              <tbody>{pcData.topupHistory.map((r,i)=><tr key={i} onMouseEnter={e=>e.currentTarget.style.background='rgba(27,59,95,0.02)'} onMouseLeave={e=>e.currentTarget.style.background=''}><td style={{...td0,fontFamily:'monospace',fontSize:12,color:sub}}>{r.date}</td><td style={{...td0,fontWeight:600,color:navy}}>{r.studentName||r.name}</td><td style={td0}>{r.room}</td><td style={td0}><TypePill type={r.type}/></td><td style={{...td0,textAlign:'right',fontFamily:'monospace',fontWeight:700,color:green}}>+{inr(r.amount)}</td><td style={td0}><Pill v={r.mode}/></td><td style={{...td0,fontFamily:'monospace',fontSize:12,color:sub}}>{r.ref}</td><td style={{...td0,fontSize:12,color:sub}}>{r.by}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(r.bal)}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
        <Divider label="Balance Sheet Reconciliation"/>
        <div style={{background:'white',borderRadius:12,padding:'22px 26px',boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:24}}>
          <RRow label={`Opening admission loads (${(pcData.ledger||[]).length} × ₹15,000)`} value={inr(pcData.admissionLoads||pcData.totalTopup)}/>
          <RRow label="+ Admin top-ups credited (all cycles)" value={`+ ${inr(pcData.adminTopups||0)}`} color={green}/>
          <RRow label="− Canteen deductions (cumulative)" value={`− ${inr(pcData.spendCanteen||0)}`} color={coral}/>
          <RRow label="− Laundry deductions (cumulative)" value={`− ${inr(pcData.spendLaundry||0)}`} color={coral}/>
          <RRow label="− Electricity deductions (cumulative)" value={`− ${inr(pcData.spendElec||0)}`} color={coral}/>
          <div style={{background:'rgba(27,59,95,0.04)',borderRadius:8,padding:'12px 16px',marginTop:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontWeight:700,fontSize:14,color:navy}}>Current liability — total balance owed to students</span>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <span style={{fontFamily:'monospace',fontSize:20,fontWeight:700,color:navy}}>{inr(pcData.totalPool||0)}</span>
              <Pill v="✓ Reconciled"/>
            </div>
          </div>
        </div>
      </>)}

      {/* ══ DEPOSIT TAB ═══════════════════════════════════════════════════════ */}
      {tab==='deposit'&&(<>
        {loading.deposit && <div style={{textAlign:'center',padding:40,color:sub}}>Loading deposit data…</div>}
        <div style={{background:'white',borderRadius:12,padding:'24px 32px',boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:24,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
          <div><h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:24,fontWeight:500,color:navy,marginBottom:4}}>Security Deposit Register</h2><p style={{fontSize:13,color:sub}}>Interest-free refundable deposits · Balance sheet liability · Not hostel income</p></div>
          <select style={sel}><option>A.Y. 2025–26</option><option>A.Y. 2024–25</option></select>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
          <SumCard label="Total Deposits Held" value={inrL(depData.totalHeld)} sub={`${(depData.ledger||[]).length} students · current liability`} accent={navy}/>
          <SumCard label="Refunds Pending"     value={inrL(depData.netRefundable)} sub={`${depData.exitCount||0} students exiting`} accent={orange} small/>
          <SumCard label="Deductions Raised"   value={inr(depData.totalDeductions)} sub="Damage / dues adjusted" accent={red} small/>
          <SumCard label="Net Refundable"      value={inrL(depData.netRefundable)} sub="After all deductions" accent={green} small/>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginBottom:22}}>
          <span style={{fontSize:11,letterSpacing:1.5,textTransform:'uppercase',color:sub,fontWeight:600}}>Filter by:</span>
          <select style={sel} value={depSt} onChange={e=>setDepSt(e.target.value)}><option value="all">All Statuses</option><option value="Active">Active</option><option value="Exit Pending">Exit Pending</option><option value="Dues Adjusted">Dues Adjusted</option><option value="Refunded">Refunded</option></select>
          <select style={sel} value={depType} onChange={e=>setDepType(e.target.value)}><option value="all">All Types</option><option value="4sharing">4 Sharing</option><option value="3sharing">3 Sharing</option><option value="2sharing">Twin</option><option value="2sharingPremium">Premium</option><option value="private">Private</option></select>
          <button onClick={()=>{setDepSt('all');setDepType('all');setDepQ('');}} style={{padding:'8px 14px',background:'transparent',border:`1.5px solid ${light}`,borderRadius:6,cursor:'pointer',fontSize:11,fontWeight:600,color:navy,fontFamily:'Work Sans,sans-serif',marginLeft:'auto'}}>✕ Clear</button>
        </div>
        <div style={{background:'white',borderRadius:12,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',overflow:'hidden',marginBottom:22}}>
          <div style={{padding:'18px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:`1px solid ${light}`,flexWrap:'wrap',gap:12}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:18,fontWeight:600,color:navy}}>Deposit Ledger</div>
            <div style={{display:'flex',alignItems:'center',gap:8,background:cream,borderRadius:8,padding:'8px 14px'}}><span>🔍</span><input placeholder="Search…" value={depQ} onChange={e=>setDepQ(e.target.value)} style={{border:'none',background:'transparent',fontFamily:'Work Sans,sans-serif',fontSize:13,outline:'none',width:150}}/></div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['ID','Student Name','Room','Type','Admission Date','Deposit Paid','Deductions','Reason','Net Refundable','Status'].map((h,i)=><th key={i} style={{...th0,textAlign:[5,6,8].includes(i)?'right':'left'}}>{h}</th>)}</tr></thead>
              <tbody>{depF.map((r,i)=><tr key={i} onMouseEnter={e=>e.currentTarget.style.background='rgba(27,59,95,0.02)'} onMouseLeave={e=>e.currentTarget.style.background=''}><td style={{...td0,fontFamily:'monospace',fontSize:12,color:sub}}>{r.admissionNumber||r.sid||'—'}</td><td style={{...td0,fontWeight:600,color:navy}}>{r.studentName||r.name}</td><td style={td0}>{r.room||'—'}</td><td style={td0}><TypePill type={r.roomType||r.type||'3sharing'}/></td><td style={{...td0,fontSize:12,color:sub}}>{r.admissionDate?new Date(r.admissionDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):r.admdate||'—'}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace'}}>{inr(r.deposit)}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace',fontWeight:700,color:r.deduction>0?red:green}}>{r.deduction>0?`−${inr(r.deduction)}`:'₹0'}</td><td style={{...td0,fontSize:12,color:sub}}>{r.reason||'—'}</td><td style={{...td0,textAlign:'right',fontFamily:'monospace',fontWeight:700}}>{inr(r.net)}</td><td style={td0}><Pill v={r.status}/></td></tr>)}</tbody>
            </table>
          </div>
          <div style={{padding:'13px 22px',display:'flex',alignItems:'center',justifyContent:'space-between',borderTop:`1px solid ${light}`,background:cream,flexWrap:'wrap',gap:8}}>
            <span style={{fontSize:12,color:sub}}>Showing {depF.length} of {depData.ledger.length} records</span>
            <span style={{fontSize:11,color:sub,fontStyle:'italic'}}>Interest-free · Carried as current liability on balance sheet</span>
          </div>
        </div>
        <Divider label="Deposit Reconciliation"/>
        <div style={{background:'white',borderRadius:12,padding:'22px 26px',boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:24}}>
          <RRow label={`Total deposits received (${(depData.ledger||[]).length} × ${inr(depData.totalHeld>0?(depData.totalHeld/(depData.ledger||[{deposit:1}]).length||15000):15000)})`} value={inr(depData.totalHeld)}/>
          <RRow label="− Deductions raised (damage / dues)" value={`− ${inr(depData.totalDeductions)}`} color={coral}/>
          <RRow label="− Refunds paid out to date" value={`− ${inr(depData.totalRefunded||0)}`} color={coral}/>
          <div style={{background:'rgba(27,59,95,0.04)',borderRadius:8,padding:'12px 16px',marginTop:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontWeight:700,fontSize:14,color:navy}}>Net deposit liability held</span>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <span style={{fontFamily:'monospace',fontSize:20,fontWeight:700,color:navy}}>{inr(depData.netRefundable||0)}</span>
              <Pill v="✓ Reconciled"/>
            </div>
          </div>
        </div>
      </>)}

    </div>
  );
}